/**
 * CIVIK — Espace web utilisateur (2026-09-02)
 * -------------------------------------------------------------------------
 * Création de compte, connexion, mot de passe oublié, vérification d'email
 * et page « Mon compte » — le tout via l'API (civix-api), comme l'app mobile.
 *
 * Routes client (rendues par App.js) :
 *   /connexion                 connexion (+ liens inscription / mot de passe oublié)
 *   /inscription               création de compte (consentements identiques à l'app)
 *   /mot-de-passe-oublie       demande de lien de réinitialisation
 *   /reinitialiser?token=      nouveau mot de passe
 *   /verifier-email?token=     validation de l'adresse email
 *   /mon-compte                profil, abonnement, sessions, export RGPD, suppression
 *
 * Le back-office (/app) reste réservé à l'administrateur : aucun lien public.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { UrgencePage } from './urgence';
import { CandidaturePage } from './candidature';

const API = '/api';

/* ── Stockage des jetons ────────────────────────────────────────────── */
const LS_ACCESS = 'civik_access_token';
const LS_REFRESH = 'civik_refresh_token';
const LS_USER = 'civik_user';

export function getStoredUser() {
  try { return JSON.parse(localStorage.getItem(LS_USER) || 'null'); } catch { return null; }
}
function storeSession({ user, accessToken, refreshToken }) {
  localStorage.setItem(LS_ACCESS, accessToken);
  localStorage.setItem(LS_REFRESH, refreshToken);
  localStorage.setItem(LS_USER, JSON.stringify(user));
}
export function clearSession() {
  localStorage.removeItem(LS_ACCESS);
  localStorage.removeItem(LS_REFRESH);
  localStorage.removeItem(LS_USER);
}

/* ── Client API (refresh automatique sur 401) ───────────────────────── */
export async function apiFetch(path, { method = 'GET', body, auth = false, retry = true, headers: extraHeaders } = {}) {
  // `headers` (2026-09-14) : en-têtes supplémentaires (interface services : X-Civik-Terminal…).
  // FormData (2026-09-15, justificatifs de candidature) : pas d'en-tête JSON, corps transmis tel quel.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers = { ...(isForm ? {} : { 'Content-Type': 'application/json' }), ...(extraHeaders || {}) };
  if (auth) {
    const token = localStorage.getItem(LS_ACCESS);
    if (!token) throw Object.assign(new Error('Non connecté'), { status: 401 });
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body !== undefined ? (isForm ? body : JSON.stringify(body)) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* réponse sans corps */ }

  if (res.status === 401 && auth && retry) {
    const refreshToken = localStorage.getItem(LS_REFRESH);
    if (refreshToken) {
      const r = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (r.ok) {
        const t = await r.json();
        if (t.accessToken) {
          localStorage.setItem(LS_ACCESS, t.accessToken);
          if (t.refreshToken) localStorage.setItem(LS_REFRESH, t.refreshToken);
          return apiFetch(path, { method, body, auth, retry: false, headers: extraHeaders });
        }
      }
      clearSession();
    }
  }
  if (!res.ok) {
    const message = (data && (data.message || data.error)) || `Erreur ${res.status}`;
    // `code` + `data` (2026-09-14) : codes applicatifs (TERMINAL_LIMIT, TERMINAL_REQUIRED…) et corps complet.
    throw Object.assign(new Error(message), { status: res.status, details: data && data.details, code: data && data.code, data });
  }
  return data;
}

/** Web app d'un opérateur selon le type de son agence (POLICE → /surete, FIRE → /securite). */
export function operatorAppUrl(user, next = '') {
  const base = user && user.agency && user.agency.kind === 'FIRE' ? '/securite' : '/surete';
  if (next && (next.startsWith('/surete') || next.startsWith('/securite'))) return next.startsWith(base) ? next : `${base}/`;
  return `${base}/`;
}

export function go(path) {
  window.history.pushState({}, '', path);
  // setTimeout : au premier rendu, l'écouteur popstate d'App.js n'est pas encore
  // attaché (les effets enfants s'exécutent avant ceux du parent).
  setTimeout(() => window.dispatchEvent(new PopStateEvent('popstate')), 0);
  window.scrollTo(0, 0);
}

/* ── Consentements (identiques à l'app mobile — src/constants/legal.ts) ── */
const CONSENT_ITEMS = [
  { id: 'age', required: true, label: 'Je certifie avoir 18 ans ou plus' },
  { id: 'terms', required: true, label: <>J'accepte les <a href="/cgu" target="_blank" rel="noreferrer">Conditions Générales d'Utilisation</a></> },
  { id: 'privacy', required: true, label: <>J'ai lu la <a href="/politique-confidentialite" target="_blank" rel="noreferrer">Politique de Confidentialité</a></> },
  { id: 'geolocation', required: true, label: "J'autorise l'enregistrement de ma position pendant mes sessions" },
  { id: 'notifications', required: false, label: "J'accepte de recevoir des notifications (optionnel)" },
];

/* ── Habillage commun ───────────────────────────────────────────────── */
export function EspaceShell({ title, children, wide }) {
  useEffect(() => {
    document.title = `${title} — CIVIK`;
    return () => { document.title = 'CIVIK - Securite Citoyenne'; };
  }, [title]);
  return (
    <div className="espace-page">
      <nav className="navbar" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
        <a href="/" className="navbar-logo">CIVI<span>K</span></a>
        <div className="navbar-links">
          <a href="/">Retour au site</a>
        </div>
      </nav>
      <main className={`espace-content ${wide ? 'espace-wide' : ''}`}>
        <h1>{title}</h1>
        {children}
      </main>
    </div>
  );
}

export function Alert({ kind, children }) {
  if (!children) return null;
  return <div className={`espace-alert espace-alert-${kind}`}>{children}</div>;
}

function Field({ label, type = 'text', value, onChange, autoComplete, required, placeholder }) {
  return (
    <label className="espace-field">
      <span>{label}{required ? ' *' : ''}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        placeholder={placeholder || ''}
      />
    </label>
  );
}

const PASSWORD_HINT = '8 caractères minimum, avec majuscule, minuscule, chiffre et symbole.';

/* ══ /connexion ══════════════════════════════════════════════════════ */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setUnverified(false); setBusy(true);
    try {
      const data = await apiFetch('/auth/login', { method: 'POST', body: { email, password } });
      storeSession(data);
      // SESSION UNIQUE (décision Marc 2026-09-10) : un seul écran de connexion
      // pour tout le monde ; seuls les droits attachés à l'email (rôle ADMIN)
      // ouvrent l'interface d'administration (/app, même origine, même
      // session). Un `?next=/app/…` (posé par /app quand la session manque)
      // n'est suivi que pour un ADMIN.
      let next = '';
      try { next = new URLSearchParams(window.location.search).get('next') || ''; } catch { /* rien */ }
      if (data.user && data.user.role === 'ADMIN') {
        window.location.href = next.startsWith('/app') ? next : '/app/';
        return;
      }
      // Opérateur police / secours (2026-09-14) : web app de SON service, même
      // origine, même session — /surete (17, dépôt Civik_FrontEnd_Surete_17) ou
      // /securite (18, dépôt Civik_FrontEnd_Securite_18).
      if (data.user && data.user.role === 'AGENCY_OPERATOR') {
        window.location.href = operatorAppUrl(data.user, next);
        return;
      }
      // Candidature professionnelle (2026-09-15) : retour au parcours après connexion.
      let wanted = next;
      try { wanted = wanted || sessionStorage.getItem('civik_next') || ''; sessionStorage.removeItem('civik_next'); } catch { /* rien */ }
      if (wanted.startsWith('/candidature')) { go(wanted); return; }
      // Urgence d'abord (décision Marc 2026-09-02) : les citoyens arrivent sur
      // les boutons Dashcam/17/18.
      go('/urgence');
    } catch (err) {
      if (err.status === 403) {
        setUnverified(true);
        setError('Votre email n’est pas encore vérifié. Consultez votre boîte de réception (et vos spams).');
      } else {
        setError(err.message);
      }
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setError(''); setBusy(true);
    try {
      await apiFetch('/auth/resend-verification', { method: 'POST', body: { email } });
      setInfo('Email de vérification renvoyé. Consultez votre boîte de réception.');
      setUnverified(false);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <EspaceShell title="Connexion">
      <form className="espace-card" onSubmit={submit}>
        <Alert kind="error">{error}</Alert>
        <Alert kind="ok">{info}</Alert>
        <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
        <Field label="Mot de passe" type="password" value={password} onChange={setPassword} autoComplete="current-password" required />
        {unverified && (
          <button type="button" className="espace-btn-secondary" onClick={resend} disabled={busy || !email}>
            Renvoyer l’email de vérification
          </button>
        )}
        <button type="submit" className="espace-btn" disabled={busy}>{busy ? '…' : 'Me connecter'}</button>
        <div className="espace-links">
          <a href="/inscription">Créer mon compte</a>
          <a href="/mot-de-passe-oublie">Mot de passe oublié ?</a>
        </div>
      </form>
    </EspaceShell>
  );
}

/* ══ /inscription ════════════════════════════════════════════════════ */
export function RegisterPage() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', password2: '', promoCode: '' });
  const [consents, setConsents] = useState({ age: false, terms: false, privacy: false, geolocation: false, notifications: false });
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [promoNote, setPromoNote] = useState('');
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.password2) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    const missing = CONSENT_ITEMS.filter((c) => c.required && !consents[c.id]);
    if (missing.length) { setError('Merci de cocher toutes les cases obligatoires.'); return; }
    setBusy(true);
    try {
      const res = await apiFetch('/auth/register', {
        method: 'POST',
        body: {
          email: form.email,
          password: form.password,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
          ...(form.promoCode.trim() ? { promoCode: form.promoCode.trim() } : {}),
          consents: CONSENT_ITEMS.map((c) => ({ id: c.id, granted: !!consents[c.id] })),
        },
      });
      // Un code refusé n'empêche jamais la création du compte : on le dit,
      // l'utilisateur pourra le ressaisir depuis « Mon compte ».
      setPromoNote(res && res.promo
        ? `Code ${res.promo.code} appliqué : accès Abonné${res.promo.expiresAt ? ` jusqu'au ${new Date(res.promo.expiresAt).toLocaleDateString('fr-FR')}` : ''}.`
        : (res && res.promoError ? `Code promo non appliqué : ${res.promoError}` : ''));
      setDone(true);
    } catch (err) {
      setError(err.details ? Object.values(err.details).join(' — ') : err.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <EspaceShell title="Vérifiez votre email">
        <div className="espace-card">
          <Alert kind="ok">
            Votre compte est créé. Un email de vérification a été envoyé à <strong>{form.email}</strong> —
            cliquez sur le lien qu’il contient (valable 24 h), puis connectez-vous.
          </Alert>
          {promoNote && <Alert kind="ok">{promoNote}</Alert>}
          <a className="espace-btn" href="/connexion">Aller à la connexion</a>
        </div>
      </EspaceShell>
    );
  }

  return (
    <EspaceShell title="Créer mon compte">
      <form className="espace-card" onSubmit={submit}>
        <Alert kind="error">{error}</Alert>
        <div className="espace-row">
          <Field label="Prénom" value={form.firstName} onChange={set('firstName')} autoComplete="given-name" required />
          <Field label="Nom" value={form.lastName} onChange={set('lastName')} autoComplete="family-name" required />
        </div>
        <Field label="Email" type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
        <Field label="Téléphone (optionnel)" type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" placeholder="06 12 34 56 78" />
        <Field label="Mot de passe" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" required />
        <p className="espace-hint">{PASSWORD_HINT}</p>
        <Field label="Confirmez le mot de passe" type="password" value={form.password2} onChange={set('password2')} autoComplete="new-password" required />
        <Field
          label="Code promo (facultatif)"
          value={form.promoCode}
          onChange={set('promoCode')}
          placeholder="Ex. BIENVENUE2026"
        />
        <p className="espace-hint">Un code partenaire ou d'invitation vous donne l'accès Abonné pour une durée définie. Vous pourrez aussi le saisir plus tard depuis « Mon compte ».</p>
        {/* Explications d'autorisations données UNE FOIS ici — en urgence, les
            boutons démarrent ensuite sans aucun écran intermédiaire. */}
        <div className="espace-permissions">
          <p><strong>Comment CIVIK utilise votre téléphone pendant une session :</strong></p>
          <p>Quand vous lancez Dashcam, 17 ou 18, CIVIK enregistre la <strong>vidéo et le son</strong> (caméra + micro) par segments envoyés sur nos serveurs en Union européenne, et associe votre <strong>position</strong> à la session — uniquement pendant qu'elle est ouverte, jamais en arrière-plan. Votre navigateur ou téléphone demandera ces autorisations au premier lancement. CIVIK n'appelle jamais les secours à votre place.</p>
        </div>
        <div className="espace-consents">
          {CONSENT_ITEMS.map((c) => (
            <label key={c.id} className="espace-consent">
              <input
                type="checkbox"
                checked={consents[c.id]}
                onChange={(e) => setConsents((s) => ({ ...s, [c.id]: e.target.checked }))}
              />
              <span>{c.label}{c.required ? ' *' : ''}</span>
            </label>
          ))}
        </div>
        <button type="submit" className="espace-btn" disabled={busy}>{busy ? '…' : 'Créer mon compte'}</button>
        <div className="espace-links"><a href="/connexion">J’ai déjà un compte</a></div>
      </form>
    </EspaceShell>
  );
}

/* ══ /mot-de-passe-oublie ════════════════════════════════════════════ */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await apiFetch('/auth/forgot-password', { method: 'POST', body: { email } });
      setSent(true);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <EspaceShell title="Mot de passe oublié">
      <form className="espace-card" onSubmit={submit}>
        <Alert kind="error">{error}</Alert>
        {sent ? (
          <Alert kind="ok">
            Si un compte existe avec cette adresse, un email de réinitialisation vient d’être envoyé
            (lien valable 1 h). Pensez à vérifier vos spams.
          </Alert>
        ) : (
          <>
            <p className="espace-hint">Saisissez l’email de votre compte : vous recevrez un lien pour créer un nouveau mot de passe.</p>
            <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
            <button type="submit" className="espace-btn" disabled={busy}>{busy ? '…' : 'Envoyer le lien'}</button>
          </>
        )}
        <div className="espace-links"><a href="/connexion">Retour à la connexion</a></div>
      </form>
    </EspaceShell>
  );
}

/* ══ /reinitialiser?token= ═══════════════════════════════════════════ */
export function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== password2) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    setBusy(true);
    try {
      await apiFetch('/auth/reset-password', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <EspaceShell title="Lien invalide">
        <div className="espace-card">
          <Alert kind="error">Ce lien de réinitialisation est incomplet. Redemandez un email depuis « Mot de passe oublié ».</Alert>
          <a className="espace-btn" href="/mot-de-passe-oublie">Mot de passe oublié</a>
        </div>
      </EspaceShell>
    );
  }

  return (
    <EspaceShell title="Nouveau mot de passe">
      <form className="espace-card" onSubmit={submit}>
        <Alert kind="error">{error}</Alert>
        {done ? (
          <>
            <Alert kind="ok">Mot de passe modifié. Vous pouvez vous connecter.</Alert>
            <a className="espace-btn" href="/connexion">Me connecter</a>
          </>
        ) : (
          <>
            <Field label="Nouveau mot de passe" type="password" value={password} onChange={setPassword} autoComplete="new-password" required />
            <p className="espace-hint">{PASSWORD_HINT}</p>
            <Field label="Confirmez le mot de passe" type="password" value={password2} onChange={setPassword2} autoComplete="new-password" required />
            <button type="submit" className="espace-btn" disabled={busy}>{busy ? '…' : 'Changer le mot de passe'}</button>
          </>
        )}
      </form>
    </EspaceShell>
  );
}

/* ══ /verifier-email?token= ══════════════════════════════════════════ */
export function VerifyEmailPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [state, setState] = useState(token ? 'pending' : 'missing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${API}/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        if (res.ok) { setState('ok'); setMessage((data && data.message) || 'Votre email est vérifié.'); }
        else { setState('error'); setMessage((data && (data.message || data.error)) || 'Lien invalide ou expiré.'); }
      })
      .catch(() => { if (!cancelled) { setState('error'); setMessage('Impossible de joindre le serveur. Réessayez.'); } });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <EspaceShell title="Vérification de l’email">
      <div className="espace-card">
        {state === 'pending' && <p className="espace-hint">Vérification en cours…</p>}
        {state === 'ok' && (
          <>
            <Alert kind="ok">{message}</Alert>
            <a className="espace-btn" href="/connexion">Me connecter</a>
          </>
        )}
        {(state === 'error' || state === 'missing') && (
          <>
            <Alert kind="error">{state === 'missing' ? 'Ce lien de vérification est incomplet.' : message}</Alert>
            <a className="espace-btn" href="/connexion">Aller à la connexion</a>
          </>
        )}
      </div>
    </EspaceShell>
  );
}

/* ══ /mon-compte ═════════════════════════════════════════════════════ */
export function AccountPage() {
  const [user, setUser] = useState(getStoredUser());
  const [profile, setProfile] = useState({ firstName: '', lastName: '', phone: '' });
  const [subscription, setSubscription] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [videos, setVideos] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [planNote, setPlanNote] = useState('');
  const [promo, setPromo] = useState(null);      // avantage en cours
  const [promoInput, setPromoInput] = useState('');
  const [promoNote, setPromoNote] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, s, sess, vids, pr] = await Promise.all([
        apiFetch('/user/profile', { auth: true }),
        apiFetch('/subscriptions/me', { auth: true }).catch(() => null),
        apiFetch('/emergency/sessions', { auth: true }).catch(() => null),
        apiFetch('/video/my-videos?limit=50', { auth: true }).catch(() => null),
        apiFetch('/promo/me', { auth: true }).catch(() => null),
      ]);
      const u = p.user || p;
      setUser(u);
      localStorage.setItem(LS_USER, JSON.stringify(u));
      setProfile({ firstName: u.firstName || '', lastName: u.lastName || '', phone: u.phone || '' });
      setSubscription(s);
      setSessions(sess ? sess.sessions || [] : []);
      setVideos(vids ? vids.videos || [] : []); // 403 SUBSCRIPTION_REQUIRED (offre gratuite) → liste vide, section masquée ci-dessous
      setPromo(pr);
    } catch (err) {
      if (err.status === 401) { clearSession(); go('/connexion'); return; }
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(LS_ACCESS)) { go('/connexion'); return; }
    load();
  }, [load]);

  const redeemPromo = async (e) => {
    e.preventDefault();
    setError(''); setPromoNote(''); setBusy(true);
    try {
      const r = await apiFetch('/promo/redeem', { method: 'POST', auth: true, body: { code: promoInput.trim() } });
      setPromoNote(
        `Code ${r.code} activé${r.expiresAt ? ` — accès Abonné jusqu'au ${new Date(r.expiresAt).toLocaleDateString('fr-FR')}` : ''}.`,
      );
      setPromoInput('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setBusy(true);
    try {
      await apiFetch('/user/profile', {
        method: 'PUT', auth: true,
        body: {
          firstName: profile.firstName.trim(),
          lastName: profile.lastName.trim(),
          ...(profile.phone.trim() ? { phone: profile.phone.trim() } : {}),
        },
      });
      setInfo('Profil mis à jour.');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const exportData = async () => {
    setError(''); setBusy(true);
    try {
      const data = await apiFetch('/user/export-data', { auth: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `civik-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    setError(''); setBusy(true);
    try {
      await apiFetch('/auth/delete-account', { method: 'DELETE', auth: true });
      clearSession();
      go('/');
    } catch (err) { setError(err.message); setBusy(false); }
  };

  const logout = () => { clearSession(); go('/'); };

  // Offre Abonné (ou ADMIN) : seule à voir ses vidéos/photos et les infos d'envoi (Marc, 2026-09-11).
  // L'offre gratuite enregistre et transmet exactement pareil ; la différence est commerciale.
  const isPaid = !!(
    (user && user.role === 'ADMIN') ||
    (subscription && (subscription.isPaid === true || (subscription.accountTier && subscription.accountTier !== 'FREE')))
  );

  /* Upgrade / downgrade : le paiement en ligne (via l'API) est le prochain
     chantier — les boutons sont en place, l'action affiche l'état réel. */
  const requestUpgrade = () => {
    setPlanNote('Le paiement en ligne arrive très prochainement : votre passage à l’offre Abonné se fera ici même, en quelques clics. Vous avez un code partenaire ou d’invitation ? Saisissez-le dans « Passer à l’offre Abonné avec un code » pour activer l’offre dès maintenant.');
  };
  const requestDowngrade = () => {
    setPlanNote('Votre demande de retour à l’offre gratuite sera gérée ici même très prochainement. En attendant, écrivez-nous à contact@civik.tech : nous la traitons manuellement.');
  };

  const fmtBytes = (b) => (b == null ? '—' : b > 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b > 1e6 ? `${(b / 1e6).toFixed(1)} Mo` : `${Math.round(b / 1e3)} Ko`);
  const fmtDur = (s) => (s == null ? '—' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`);

  const watchVideo = async (id) => {
    setError('');
    try {
      const d = await apiFetch(`/video/stream/${id}`, { auth: true });
      window.open(d.streamUrl, '_blank', 'noopener');
    } catch (err) { setError(err.message); }
  };
  // « Supprimer » côté client (règle Marc 2026-09-11) : retire la vidéo de
  // l'historique (masquage serveur) — CIVIK la conserve sur S3 le temps légal.
  // Même comportement que l'app mobile.
  const hideVideo = async (id) => {
    setError('');
    if (!window.confirm('Retirer cette vidéo de votre historique ? Elle ne sera plus visible ni accessible depuis votre compte. CIVIK la conserve le temps prévu par la loi.')) return;
    try {
      await apiFetch('/video/hide', { method: 'POST', auth: true, body: { videoIds: [id] } });
      setVideos((list) => (list ? list.filter((v) => v.id !== id) : list));
    } catch (err) { setError(err.message); }
  };
  const downloadVideo = async (id) => {
    setError('');
    try {
      const d = await apiFetch(`/video/${id}/download`, { auth: true });
      const a = document.createElement('a');
      a.href = d.downloadUrl;
      a.download = d.fileName || 'video-civik.mp4';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { setError(err.message); }
  };

  return (
    <EspaceShell title="Mon compte" wide>
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{info}</Alert>

      {/* Lancer une session depuis le navigateur (webapp) */}
      <a href="/urgence" className="espace-launch">
        <span className="espace-launch-title">🎥 Lancer une session</span>
        <span className="espace-launch-sub">Dashcam · 17 Police · 18 Secours — directement depuis ce navigateur</span>
      </a>

      <div className="espace-grid">
        {/* Profil */}
        <form className="espace-card" onSubmit={saveProfile}>
          <h2>Profil</h2>
          {user && <p className="espace-hint">Connecté en tant que <strong>{user.email}</strong></p>}
          {user && user.role === 'ADMIN' && (
            <p className="espace-hint"><a href="/app">→ Accéder à l’interface d’administration</a></p>
          )}
          <div className="espace-row">
            <Field label="Prénom" value={profile.firstName} onChange={(v) => setProfile((p) => ({ ...p, firstName: v }))} required />
            <Field label="Nom" value={profile.lastName} onChange={(v) => setProfile((p) => ({ ...p, lastName: v }))} required />
          </div>
          <Field label="Téléphone" type="tel" value={profile.phone} onChange={(v) => setProfile((p) => ({ ...p, phone: v }))} />
          <button type="submit" className="espace-btn" disabled={busy}>Enregistrer</button>
          <div className="espace-links">
            <a href="/mot-de-passe-oublie">Changer mon mot de passe (lien par email)</a>
          </div>
        </form>

        {/* Mon offre — upgrade / downgrade */}
        <div className="espace-card">
          <h2>Mon offre</h2>
          {subscription === null ? (
            <p className="espace-hint">Chargement…</p>
          ) : (
            <>
              <div className={`espace-plan ${!isPaid ? 'espace-plan-current' : ''}`}>
                <div className="espace-plan-head"><strong>Gratuite</strong><span>0 €/mois</span></div>
                <p className="espace-hint">Dashcam (24 h), boutons 17/18 (90 jours), historique.</p>
                {!isPaid ? (
                  <span className="espace-plan-badge">Votre offre actuelle</span>
                ) : (
                  <button type="button" className="espace-btn-secondary" onClick={requestDowngrade}>Revenir à l’offre gratuite</button>
                )}
              </div>
              <div className={`espace-plan ${isPaid ? 'espace-plan-current' : ''}`}>
                <div className="espace-plan-head"><strong>Abonné CIVIK</strong><span>10 €/mois</span></div>
                <p className="espace-hint">Professionnels partenaires informés à proximité, rétention étendue, historique enrichi.</p>
                {isPaid ? (
                  <>
                    <span className="espace-plan-badge">Votre offre actuelle</span>
                    {subscription.subscription && (
                      <p className="espace-hint">Période en cours jusqu’au {new Date(subscription.subscription.periodEnd).toLocaleDateString('fr-FR')}.</p>
                    )}
                  </>
                ) : (
                  <button type="button" className="espace-btn" onClick={requestUpgrade}>Passer à l’offre Abonné</button>
                )}
              </div>
              {planNote && <Alert kind="ok">{planNote}</Alert>}
            </>
          )}
        </div>

        {/* Code promo — proposé UNIQUEMENT pour le passage à l'offre Abonné (Marc, 2026-09-11) :
            un code à 100 % suffit, sans passerelle de paiement. Un abonné payant ne le voit pas ;
            un abonné par code voit l'état de son avantage. */}
        {(!isPaid || (promo && promo.active)) && (
        <div className="espace-card">
          <h2>{promo && promo.active ? 'Code promo' : 'Passer à l’offre Abonné avec un code'}</h2>
          {promo && promo.active ? (
            <>
              <p>
                Code <strong>{promo.code}</strong> actif
                {promo.discountPercent != null ? ` — ${promo.discountPercent} % de réduction` : ''}.
              </p>
              <p className="espace-hint">
                {promo.expiresAt
                  ? `Accès Abonné jusqu'au ${new Date(promo.expiresAt).toLocaleDateString('fr-FR')}.`
                  : 'Accès Abonné sans limite de durée.'}
              </p>
            </>
          ) : (
            <p className="espace-hint">Vous avez reçu un code partenaire ou d'invitation ? Saisissez-le pour activer les fonctions Abonné.</p>
          )}
          {!(promo && promo.active) && (
            <form onSubmit={redeemPromo}>
              <Field label="Saisir un code" value={promoInput} onChange={setPromoInput} placeholder="Ex. BIENVENUE2026" />
              <button type="submit" className="espace-btn" disabled={busy || !promoInput.trim()}>Activer le code</button>
            </form>
          )}
          {promoNote && <Alert kind="ok">{promoNote}</Alert>}
        </div>
        )}

        {/* Sessions */}
        <div className="espace-card espace-span2">
          <h2>Mes sessions</h2>
          {sessions === null ? (
            <p className="espace-hint">Chargement…</p>
          ) : sessions.length === 0 ? (
            <p className="espace-hint">Aucune session pour le moment. Elles apparaîtront ici après vos enregistrements (dashcam, 17, 18) — depuis ce site (« Lancer une session ») ou l’application mobile.</p>
          ) : (
            <div className="espace-table-wrap">
              <table className="espace-table">
                <thead>
                  <tr><th>Date</th><th>Type</th><th>Statut</th></tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td>{new Date(s.startedAt).toLocaleString('fr-FR')}</td>
                      <td>{s.triggerMode === 'DASHCAM' ? 'Dashcam' : s.triggerMode === 'POLICE_17' ? '17 — Police' : s.triggerMode === 'FIRE_18' ? '18 — Secours' : s.triggerMode}</td>
                      <td>{s.status === 'CLOSED' ? 'Clôturée' : s.status === 'RECORDING' ? 'En cours' : s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mes vidéos */}
        <div className="espace-card espace-span2">
          <h2>Mes vidéos</h2>
          {!isPaid ? (
            <p className="espace-hint">La consultation et le téléchargement de vos vidéos et photos, ainsi que le suivi de leurs envois, font partie de l’offre Abonné CIVIK. Vos enregistrements sont bien transmis et conservés selon les durées prévues.</p>
          ) : videos === null ? (
            <p className="espace-hint">Chargement…</p>
          ) : videos.length === 0 ? (
            <p className="espace-hint">Aucune vidéo pour le moment. Vos enregistrements (dashcam, 17, 18) apparaîtront ici, consultables et téléchargeables.</p>
          ) : (
            <div className="espace-table-wrap">
              <table className="espace-table">
                <thead>
                  <tr><th>Date</th><th>Fichier</th><th>Durée</th><th>Taille</th><th>Expire</th><th></th></tr>
                </thead>
                <tbody>
                  {videos.map((v) => (
                    <tr key={v.id}>
                      <td>{new Date(v.recordedAt).toLocaleString('fr-FR')}</td>
                      <td>{v.displayName || v.fileName}</td>
                      <td>{fmtDur(v.durationSeconds)}</td>
                      <td>{fmtBytes(v.fileSizeBytes)}</td>
                      <td>{v.expiresAt ? new Date(v.expiresAt).toLocaleDateString('fr-FR') : '—'}</td>
                      <td className="espace-video-actions">
                        <button type="button" className="espace-btn-secondary" onClick={() => watchVideo(v.id)}>Voir</button>
                        <button type="button" className="espace-btn-secondary" onClick={() => downloadVideo(v.id)}>Télécharger</button>
                        <button type="button" className="espace-btn-danger" onClick={() => hideVideo(v.id)}>Supprimer</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Données */}
        <div className="espace-card espace-span2">
          <h2>Mes données</h2>
          <p className="espace-hint">Export complet de vos données (RGPD) au format JSON, ou suppression définitive du compte.</p>
          <div className="espace-actions">
            <button type="button" className="espace-btn-secondary" onClick={exportData} disabled={busy}>Exporter mes données</button>
            <button type="button" className="espace-btn-secondary" onClick={logout}>Me déconnecter</button>
            {!confirmDelete ? (
              <button type="button" className="espace-btn-danger" onClick={() => setConfirmDelete(true)}>Supprimer mon compte</button>
            ) : (
              <span className="espace-confirm">
                Suppression définitive et irréversible (vos données personnelles sont anonymisées).
                <button type="button" className="espace-btn-danger" onClick={deleteAccount} disabled={busy}>Confirmer la suppression</button>
                <button type="button" className="espace-btn-secondary" onClick={() => setConfirmDelete(false)}>Annuler</button>
              </span>
            )}
          </div>
        </div>
      </div>
    </EspaceShell>
  );
}

/* ══ /suppression-compte — page PUBLIQUE (exigée par Google Play) ════ */
export function AccountDeletionPage() {
  useEffect(() => {
    document.title = 'Supprimer votre compte CIVIK';
    return () => { document.title = 'CIVIK - Securite Citoyenne'; };
  }, []);
  return (
    <EspaceShell title="Supprimer votre compte CIVIK">
      <div className="espace-card">
        <p className="espace-hint">
          Cette page explique comment supprimer votre compte <strong>CIVIK</strong> (application mobile et espace web,
          éditeur : MMI Industries) et ce qu'il advient de vos données.
        </p>
        <h2>Supprimer votre compte</h2>
        <p className="espace-hint">
          Connectez-vous puis, dans « Mon compte », section « Mes données », choisissez
          <strong> « Supprimer mon compte »</strong> et confirmez. La même action est disponible dans
          l'application mobile (Profil → Supprimer mon compte). La suppression est <strong>immédiate et irréversible</strong>.
        </p>
        <h2>Données supprimées</h2>
        <p className="espace-hint">
          Vos données personnelles (nom, prénom, email, téléphone) sont supprimées ou anonymisées,
          vos jetons de connexion sont révoqués, et vos vidéos suivent leur durée de rétention puis sont
          effacées définitivement (dashcam : 24 heures ; séquences d'urgence : 90 jours). Aucune donnée
          n'est conservée à des fins commerciales.
        </p>
        <h2>Sans compte, ou en cas de difficulté</h2>
        <p className="espace-hint">
          Vous pouvez aussi demander la suppression par email à
          {' '}<a href="mailto:contact@civik.tech">contact@civik.tech</a> depuis l'adresse associée au compte.
        </p>
        <a className="espace-btn" href="/connexion">Me connecter pour supprimer mon compte</a>
      </div>
    </EspaceShell>
  );
}

/* ── Table de routage exportée vers App.js ──────────────────────────── */
export const ESPACE_ROUTES = {
  '/candidature': CandidaturePage,
  '/connexion': LoginPage,
  '/inscription': RegisterPage,
  '/mot-de-passe-oublie': ForgotPasswordPage,
  '/reinitialiser': ResetPasswordPage,
  '/verifier-email': VerifyEmailPage,
  '/mon-compte': AccountPage,
  '/urgence': UrgencePage,
  '/suppression-compte': AccountDeletionPage,
};
